import { centsToEuros, parseMoneyToCents } from '@agos/shared'

import type { CartTotals, DiscountMode } from './cart-math'
import type { OrderCartLine } from './cart'

/**
 * Turning a cart into the order the API stores.
 *
 * Three translations happen here and each of them loses information if it is
 * skipped:
 *
 *   - a **gift** has a synthetic negative id and no product row, so it is sent
 *     as `productId: 0` with the rule recorded alongside it;
 *   - a customised pizza's **toppings** are folded into `customName` as
 *     `Margherita [Šunka]`, because the line has nowhere else to carry them —
 *     and the kitchen parses that bracket straight back out;
 *   - a **restaurant** order's customer name is the table, which is how every
 *     other screen finds it again.
 *
 * Ported from legacy `pages/new-order.tsx`. Pure, so the shape is a test rather
 * than a thing discovered in production.
 */

export interface OrderLinePayload {
  productId: number
  /** §9.85 — the size's label, `"50 cm"`. */
  size?: string
  quantity: number
  unitPrice: number
  customName: string
  boxGroupId?: string
  boxGroupName?: string
  boxSlotLabel?: string
  lunchMealType?: string
  giftRuleId?: number
  giftRuleType?: string
  parts?: { productId?: number; name: string; unitPrice: number; quantity: number }[]
  /**
   * Which rule bounds this composed line — `BACKEND.md` §10A.26.
   *
   * `'kombo'` means the dearest part rather than the sum, because a kombo is priced `max` by design.
   * Absent on everything else, which is what keeps the field additive.
   */
  composition?: 'kombo'
  /**
   * Extra toppings on a catalogue pizza — `BACKEND.md` §10A.80.
   *
   * Which ingredient and how many portions, never what it costs: the server prices each from
   * `pizza_ingredients` at this line's size. `unitPrice` above carries the surcharge for the
   * screen's sake and the server has always discarded it, which is why this field exists.
   */
  extras?: { ingredientId: number; quantity: number }[]
}

export interface OrderPayload {
  type: string
  paymentMethod: string
  customerName?: string
  customerPhone?: string
  customerEmail?: string
  customerAddress?: string
  deliveryLat?: number
  deliveryLon?: number
  deliveryCost: number
  discount: number
  notes?: string
  isPreorder?: boolean
  scheduledAt?: string
  employeeId?: number
  manualDiscountType?: DiscountMode
  manualDiscountValue?: number
  manualDiscountOperator?: string | null
  couponCode?: string
  items: OrderLinePayload[]
}

export interface OrderDraft {
  cart: OrderCartLine[]
  totals: CartTotals
  orderType: string
  paymentMethod: string
  tableName?: string
  customerName?: string
  customerPhone?: string
  customerEmail?: string
  customerAddress?: string
  coordinates?: { lat: number; lng: number } | null
  notes?: string
  isPreorder?: boolean
  /** Already an ISO instant; the caller owns the Bratislava conversion. */
  scheduledAt?: string | null
  employeeId?: number | null
  /**
   * Whether the channel seats people, and whether it needs an address - §9.393.
   *
   * The screen computes both from the channel table (`requirementsFor`) before it renders the fields
   * that collect them, and then this file decided the same two things from the slug. Optional and
   * falling back to the slug check, so a caller that does not pass them is unchanged.
   */
  seatsGuests?: boolean
  needsAddress?: boolean
  discountMode: DiscountMode
  discountValue: string
  couponCode?: string | null
  operatorName?: string | null
}

/** Empty strings are not values; the API wants the key absent. */
function optional(value: string | undefined): string | undefined {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}

export function orderLinePayload(line: OrderCartLine): OrderLinePayload {
  return {
    // A gift, a custom pizza and a lunch meal all carry a synthetic negative
    // id. None of them has a product row, so none of them can be stored with
    // one.
    productId: line.productId < 0 ? 0 : line.productId,
    quantity: line.quantity,
    unitPrice: centsToEuros(parseMoneyToCents(line.unitPrice)),
    customName: line.pizzaExtras ? `${line.productName} [${line.pizzaExtras}]` : line.productName,
    // §9.85 — which size, so `ServerPricing` prices it from the product's own `pizza_sizes` rather
    // than from its base price (`BACKEND.md` §10A.33). Absent for a product sold at one price.
    ...(line.size === undefined ? {} : { size: line.size }),
    ...(line.boxGroupId ? { boxGroupId: line.boxGroupId } : {}),
    ...(line.boxGroupName ? { boxGroupName: line.boxGroupName } : {}),
    ...(line.boxSlotLabel ? { boxSlotLabel: line.boxSlotLabel } : {}),
    // §10A.88 — which course of the day's menu, so the kitchen can box it. Stored and published,
    // never priced from; absent on everything that is not a lunch line.
    ...(line.lunchMealType ? { lunchMealType: line.lunchMealType } : {}),
    ...(line.giftRuleId !== undefined ? { giftRuleId: line.giftRuleId } : {}),
    ...(line.giftRuleType !== undefined ? { giftRuleType: line.giftRuleType } : {}),
    // §10A.14. Cents on the cart, euros on the wire — the same conversion `unitPrice` above makes,
    // because the server bounds the line against these and the two figures have to be comparable.
    ...(line.parts && line.parts.length > 0
      ? {
          parts: line.parts.map((part) => ({
            ...(part.productId !== undefined ? { productId: part.productId } : {}),
            name: part.name,
            unitPrice: centsToEuros(part.unitPriceCents),
            quantity: part.quantity,
          })),
        }
      : {}),
    // §10A.26. Which rule bounds it: a kombo by its dearest pizza, everything else by the sum. The
    // server will not infer this, so a line that does not say is bounded exactly as it was before.
    ...(line.composition ? { composition: line.composition } : {}),
    // §10A.80 — which toppings, never what they cost. `unitPrice` above already carries the
    // surcharge and the server has always discarded it: every positive `productId` is re-read from
    // `products`, so §9.41's ham was free from the day it shipped. This is the field that charges
    // for it, and `customName` keeps the text the kitchen reads.
    ...(line.extrasChosen && line.extrasChosen.length > 0
      ? { extras: line.extrasChosen.map((each) => ({ ...each })) }
      : {}),
  }
}

export function buildOrderPayload(draft: OrderDraft): OrderPayload {
  // §9.393 - the capability the screen already asked for, then the slug. `na_sekeru` uses tables and
  // the builder collects one; naming the order by the literal `restaurant` threw it away, and the
  // table name is what the room screens key on (§9.383).
  const isDelivery = draft.needsAddress ?? draft.orderType === 'delivery'
  const isRestaurant = draft.seatsGuests ?? draft.orderType === 'restaurant'
  const totalDiscountCents = draft.totals.manualDiscountCents + draft.totals.couponDiscountCents

  return {
    type: draft.orderType,
    paymentMethod: draft.paymentMethod,
    // A table order is found again by its customer name on four other screens.
    customerName: isRestaurant
      ? `Stôl ${draft.tableName?.trim() ?? ''}`.trim()
      : optional(draft.customerName),
    customerPhone: optional(draft.customerPhone),
    customerEmail: optional(draft.customerEmail),
    customerAddress: optional(draft.customerAddress),
    ...(isDelivery && draft.coordinates
      ? { deliveryLat: draft.coordinates.lat, deliveryLon: draft.coordinates.lng }
      : {}),
    // Never charged on a collection, whatever zone the address falls in.
    deliveryCost: isDelivery ? centsToEuros(draft.totals.deliveryCents) : 0,
    discount: centsToEuros(totalDiscountCents),
    notes: optional(draft.notes),
    ...(draft.isPreorder ? { isPreorder: true } : {}),
    ...(draft.scheduledAt ? { scheduledAt: draft.scheduledAt } : {}),
    ...(draft.employeeId != null ? { employeeId: draft.employeeId } : {}),
    // The discount is recorded with who gave it. A discount nobody is named for
    // is a discount nobody can be asked about.
    ...(draft.totals.manualDiscountCents > 0
      ? {
          manualDiscountType: draft.discountMode,
          manualDiscountValue: Number(draft.discountValue),
          manualDiscountOperator: draft.operatorName ?? null,
        }
      : {}),
    ...(draft.couponCode && draft.totals.couponDiscountCents > 0
      ? { couponCode: draft.couponCode }
      : {}),
    items: draft.cart.map(orderLinePayload),
  }
}
